import styled from "@emotion/styled"
import { IconButton, Menu, MenuItem, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import { getNonUmbracoUrl, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { Desktop, Mobile } from "../../common/bit/MediaQueries"
import Message from "../../icons/Message"
import MessageSmall from "../../icons/MessageSmall"
import { sharedColors } from "../../theme/colors"
import { HeaderData } from "../Layout"
import ContactDrawer from "./ContactDrawer"

const OuterIconButton = styled(IconButton)`
  height: 100%;
  padding: 0;
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

export const StyledIconButton = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "active",
})<{ active: boolean }>`
  padding: 0;
  margin: 0;
  height: 32px;
  width: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    height: 40px;
    width: 40px;
  }
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ active }) => active && `background-color: #F5F6F8;`}
  &:hover {
    background-color: white;
    border: 1px solid ${sharedColors.brandDark};
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
  padding: 16px 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  align-items: center;
  color: ${sharedColors.black};
`

const StyledMenu = styled(Menu)`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
`

const StyledMenuItemText = styled.div`
  padding: 16px 8px !important;
  border-bottom: 1px solid #d1d5db;
`

export default function ContactMenu({ header }: { header: HeaderData }) {
  const t = useTranslations("Homepage")
  const [isOpen, setIsOpen] = useState(false)
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen(true)
    setAnchorEl(event.currentTarget)
  }
  const handleDrawerClick = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setIsOpen(false)
    setAnchorEl(null)
  }

  return (
    <>
      <Desktop centerHorizontally height="100%">
        <OuterIconButton
          onClick={handleClick}
          disableRipple
          aria-label={t("contactAria")}
        >
          <StyledIconButton tabIndex={-1} active={isOpen}>
            <Message />
          </StyledIconButton>
        </OuterIconButton>
      </Desktop>
      <Mobile>
        <OuterIconButton
          onClick={handleDrawerClick}
          disableRipple
          aria-label={t("contactAria")}
        >
          <StyledIconButton tabIndex={-1} active={isOpen}>
            <MessageSmall />
          </StyledIconButton>
        </OuterIconButton>
      </Mobile>

      <ContactDrawer open={open} setOpen={setOpen} header={header} />

      <StyledMenu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          transform: "translateX(-12px) !important",
          "& .MuiMenu-list": {
            padding: "0 8px",
          },
          "& .MuiPaper-root": {
            padding: 0,
            boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.16)",
          },
          "& .MuiMenuItem-root": {
            "&:not(:last-child)": {
              borderBottom: "1px solid #D1D5DB",
            },
          },
          zIndex: 102,
        }}
      >
        <StyledMenuItemText>
          <Typography variant="subtitle1">{header.contactTitle}</Typography>
        </StyledMenuItemText>

        {header.itemsList.map((item) => (
          <MenuItem key={item.label} onClick={handleClose}>
            <StyledLink href={getNonUmbracoUrl(item.link)}>
              <Image
                src={getUmbracoMediaUrl(item.icon)}
                alt={item.label}
                width={30}
                height={30}
              />
              {item.label}
            </StyledLink>
          </MenuItem>
        ))}
      </StyledMenu>
    </>
  )
}
